import { useContext, useEffect, useMemo, useState } from 'react';

import { FeatureFlagsContext } from 'components/shared/feature-flags';

import { fetchMonitoringSettings } from 'api/monitoring';

import { useCurrentUser } from 'hooks/api/use-current-user';

import {
  resolveStageKey,
  areAllCriticalElementsRestricted,
} from 'helpers/utils/monitoring-visibility';

import { DATE_FORMAT } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

interface FeatureFlagsContextValue {
  featureFlags: Record<string, boolean>;
}

interface CurrentUserShape {
  data?: {
    oktaUserId?: string;
    client?: { id?: string | number };
  };
}

export interface UseGoalMonitoringVisibilityResult {
  stageKey: string | null;
  allRestricted: boolean;
  shouldShowMonitoring: boolean;
  resolved: boolean;
  isLoading: boolean;
}

const useGoalMonitoringVisibility = (
  criticalElements: unknown,
  isMonitoringYear: boolean,
  clientIdOverride: string | number | null = null
): UseGoalMonitoringVisibilityResult => {
  const { featureFlags } = useContext(
    FeatureFlagsContext
  ) as FeatureFlagsContextValue;
  const currentUser = useCurrentUser() as CurrentUserShape;

  const [stageKey, setStageKey] = useState<string | null>(null);
  const [resolved, setResolved] = useState(false);
  const [hasMonitoringSettings, setHasMonitoringSettings] = useState(false);

  useEffect(() => {
    const fetchStage = async (): Promise<void> => {
      setResolved(false);

      try {
        const userId = currentUser?.data?.oktaUserId;
        const clientId = clientIdOverride || currentUser?.data?.client?.id;

        if (!userId || !clientId) {
          setStageKey(null);
          setHasMonitoringSettings(false);
          setResolved(true);

          return;
        }

        const settings = await fetchMonitoringSettings(userId, clientId);
        const monitoring = settings?.monitoring;

        if (!monitoring?.cohort) {
          setStageKey(null);
          setHasMonitoringSettings(false);
          setResolved(true);

          return;
        }

        const { inquiriesToApps, applicationsToAdmits, admitsToEnrollment } =
          monitoring;

        const stage = resolveStageKey(
          { inquiriesToApps, applicationsToAdmits, admitsToEnrollment },
          DATE_FORMAT
        );

        setStageKey(stage);
        setHasMonitoringSettings(true);
        setResolved(true);
      } catch {
        setStageKey(null);
        setHasMonitoringSettings(false);
        setResolved(true);
      }
    };

    if (featureFlags['goals-monitoring'] && isMonitoringYear) {
      fetchStage();
    } else {
      setStageKey(null);
      setHasMonitoringSettings(false);
      setResolved(true);
    }
  }, [
    featureFlags['goals-monitoring'],
    isMonitoringYear,
    currentUser?.data?.oktaUserId,
    currentUser?.data?.client?.id,
    clientIdOverride,
  ]);

  const allRestricted = useMemo(() => {
    if (!resolved) return false;

    return areAllCriticalElementsRestricted(
      Array.isArray(criticalElements) ? criticalElements : []
    );
  }, [criticalElements, resolved]);

  const baseConditions =
    !!featureFlags['goals-monitoring'] &&
    !!isMonitoringYear &&
    !!(criticalElements as { length?: number })?.length;

  const isLoading = baseConditions && !resolved;

  const shouldShowMonitoring =
    baseConditions && resolved && !allRestricted && hasMonitoringSettings;

  return {
    stageKey,
    allRestricted,
    shouldShowMonitoring,
    resolved,
    isLoading,
  };
};

export default useGoalMonitoringVisibility;
